"use client"

import { useState } from "react"

import { useTranslations } from "next-intl"

import { ActiveFilters } from "@/components/marketplace/listing/active-filters"
import { FacetChipRow } from "@/components/marketplace/listing/facet-chip-row"
import { FilterSheet } from "@/components/marketplace/listing/filter-sheet"
import { LISTING_CONTROL_CLASS } from "@/components/marketplace/listing/listing-control-styles"
import type { FacetOption } from "@/components/marketplace/listing/types"
import { CountBadge, FilterIcon, Pill } from "@/components/ui"
import type { SearchFacet, SearchQuery } from "@/lib/types/search"
import { trackSelectContent } from "@/platform/observability/analytics/v2/search"

interface FilterBarProps {
  query: SearchQuery
  categoryOptions?: FacetOption[]
  categoryLabels?: Record<string, string>
  facets?: SearchFacet[]
  filterCount: number
  resultCount?: number
  onUpdateQuery: (updates: Partial<SearchQuery>) => void
  onClearAll: () => void
  showActiveFilters?: boolean
  className?: string
}

export const FilterBar = ({
  query,
  categoryOptions,
  categoryLabels,
  facets,
  filterCount,
  resultCount,
  onUpdateQuery,
  onClearAll,
  showActiveFilters = true,
  className,
}: FilterBarProps) => {
  const t = useTranslations("search")
  const [open, setOpen] = useState(false)

  const openSheet = () => {
    trackSelectContent({ content_type: "filter_menu", content_id: "open" })
    setOpen(true)
  }

  return (
    <>
      <div className={`bg-palette-canvas mx-gut-bleed px-gut top-sticky-chrome z-dropdown sticky py-250 ${className ?? ""}`}>
        <FacetChipRow
          query={query}
          onUpdateQuery={onUpdateQuery}
          leading={
            <Pill size="sm" className={`${LISTING_CONTROL_CLASS} bg-brand text-fg-inverted hover:bg-brand-hover border border-transparent`} onClick={openSheet}>
              {t("filters")}
              {filterCount > 0 ? (
                <CountBadge count={filterCount} minimumDigits={2} className="bg-surface-paper text-brand font-text text-tab min-w-7.5 px-200 font-normal" />
              ) : (
                <FilterIcon size={16} />
              )}
            </Pill>
          }
        />
        {showActiveFilters && (
          <ActiveFilters query={query} categoryLabels={categoryLabels} facets={facets} onUpdateQuery={onUpdateQuery} onClearAll={onClearAll} className="mt-300" />
        )}
      </div>

      <FilterSheet
        open={open}
        onClose={setOpen}
        query={query}
        categoryOptions={categoryOptions}
        facets={facets}
        filterCount={filterCount}
        resultCount={resultCount}
        onUpdateQuery={onUpdateQuery}
        onClearAll={onClearAll}
      />
    </>
  )
}
